import DataTable from 'v2/components/DataTable';
import EmptyState from 'v2/components/EmptyState';
import LoadingBar from 'v2/components/LoadingBar';
import OutlinedButton from 'v2/components/OutlinedButton';
import useAircraftTable from 'v2/layouts/OrganizationWorkspaceLayout/components/AircraftTableSection/hooks/useAircraftTable';
import EquipmentRowActions from 'v2/layouts/OrganizationWorkspaceLayout/components/EquipmentRowActions';

import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import {
    ACTIONS_COLUMN_KEY,
    ACTIONS_COLUMN_WIDTH,
    EMPTY_CELL_TEXT,
    FLIGHT_CONTROLLER_SERIAL_COLUMN_KEY,
    MAKE_COLUMN_KEY,
    MODEL_COLUMN_KEY,
    NAME_COLUMN_KEY,
    REGISTRATION_NUMBER_COLUMN_KEY,
    SERIAL_NUMBER_COLUMN_KEY,
    SWATH_WIDTH_COLUMN_KEY,
    TEST_ID
} from './constants';
import type { DataTableColumn, DataTableRow, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const AircraftTableSection: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const { aircraft, handleDelete, handleLoadMore, hasNextPage, isFetchingMore, isLoading } =
        useAircraftTable();

    const { navigate } = useNavigation();
    const { editAircraftPage } = usePageRoutes();

    const {
        ACTIONS_LABEL,
        DELETE,
        EDIT,
        EMPTY_DESCRIPTION,
        EMPTY_HEADING,
        FLIGHT_CONTROLLER_SERIAL,
        LOAD_MORE,
        MAKE,
        MODEL,
        NAME,
        REGISTRATION_NUMBER,
        SERIAL_NUMBER,
        SWATH_WIDTH,
        TABLE_LABEL
    } = useTranslation();

    const columns: DataTableColumn[] = [
        { key: NAME_COLUMN_KEY, label: NAME },
        { key: MAKE_COLUMN_KEY, label: MAKE },
        { key: MODEL_COLUMN_KEY, label: MODEL },
        { key: SERIAL_NUMBER_COLUMN_KEY, label: SERIAL_NUMBER },
        { key: REGISTRATION_NUMBER_COLUMN_KEY, label: REGISTRATION_NUMBER },
        { key: FLIGHT_CONTROLLER_SERIAL_COLUMN_KEY, label: FLIGHT_CONTROLLER_SERIAL },
        { key: SWATH_WIDTH_COLUMN_KEY, label: SWATH_WIDTH },
        { key: ACTIONS_COLUMN_KEY, label: '', width: ACTIONS_COLUMN_WIDTH }
    ];

    const rows: DataTableRow[] = aircraft.map(currentAircraft => ({
        [ACTIONS_COLUMN_KEY]: (
            <EquipmentRowActions
                ariaLabel={ACTIONS_LABEL}
                deleteText={DELETE}
                editText={EDIT}
                onDelete={() => handleDelete(currentAircraft.id)}
                onEdit={() => navigate(editAircraftPage(currentAircraft.id))}
            />
        ),
        [FLIGHT_CONTROLLER_SERIAL_COLUMN_KEY]:
            currentAircraft.flightControllerSerial || EMPTY_CELL_TEXT,
        [MAKE_COLUMN_KEY]: currentAircraft.make || EMPTY_CELL_TEXT,
        [MODEL_COLUMN_KEY]: currentAircraft.model || EMPTY_CELL_TEXT,
        [NAME_COLUMN_KEY]: currentAircraft.nickname || EMPTY_CELL_TEXT,
        [REGISTRATION_NUMBER_COLUMN_KEY]: currentAircraft.registrationNumber || EMPTY_CELL_TEXT,
        [SERIAL_NUMBER_COLUMN_KEY]: currentAircraft.serialNumber || EMPTY_CELL_TEXT,
        [SWATH_WIDTH_COLUMN_KEY]: currentAircraft.defaultSwathWidthMeters ?? EMPTY_CELL_TEXT
    }));

    if (isLoading) {
        return <LoadingBar className={className} testId={testId} />;
    }

    if (aircraft.length === 0) {
        return (
            <EmptyState
                className={className}
                description={EMPTY_DESCRIPTION}
                heading={EMPTY_HEADING}
                testId={testId}
            />
        );
    }

    return (
        <div className={`${className} ${styles.aircraftTableSection}`} data-testid={testId}>
            <div className={styles.tableScroll}>
                <DataTable ariaLabel={TABLE_LABEL} columns={columns} rows={rows} />
            </div>

            {hasNextPage ? (
                <OutlinedButton
                    className={styles.loadMore}
                    disabled={isFetchingMore}
                    onClick={handleLoadMore}
                >
                    {LOAD_MORE}
                </OutlinedButton>
            ) : null}
        </div>
    );
};

export default AircraftTableSection;
